import type { Membership, Role, Workspace, WorkspaceRole } from "../api/types";
import { titleCase } from "../lib/format";

export type Permission =
  | "tenant.manage"
  | "users.manage"
  | "integrations.manage"
  | "venues.manage"
  | "calendar.view"
  // Running the client's own programme and letting the space to other people are two
  // different jobs, so they are two different capabilities. Mirrors identity.PermissionManageEvents.
  | "events.manage"
  | "bookings.manage"
  | "bookings.tasks.manage"
  | "customers.manage"
  // Writing to a customer is daily coordination work rather than integration
  // administration, so it is a capability of its own. Mirrors identity.PermissionSendEmail.
  | "emails.send"
  | "quotes.manage"
  | "invoices.issue"
  | "payments.record"
  | "refunds.manage"
  | "sponsors.manage"
  // Hiring somebody in commits the client's money to a third party, so it is a capability of its
  // own rather than a corner of managing venues. Mirrors identity.PermissionManageVendors.
  | "vendors.manage"
  | "inventory.manage"
  | "inventory.operate"
  | "exports.reports"
  | "analytics.view"
  | "privacy.manage";

const permissions: Record<Role, Set<Permission>> = {
  owner: new Set([
    "tenant.manage",
    "users.manage",
    "integrations.manage",
    "venues.manage",
    "calendar.view",
    "events.manage",
    "bookings.manage",
    "bookings.tasks.manage",
    "customers.manage",
    "emails.send",
    "quotes.manage",
    "invoices.issue",
    "payments.record",
    "refunds.manage",
    "sponsors.manage",
    "vendors.manage",
    "inventory.manage",
    "inventory.operate",
    "exports.reports",
    "analytics.view",
    "privacy.manage",
  ]),
  administrator: new Set([
    "users.manage",
    "integrations.manage",
    "venues.manage",
    "calendar.view",
    "events.manage",
    "bookings.manage",
    "bookings.tasks.manage",
    "customers.manage",
    "emails.send",
    "quotes.manage",
    "invoices.issue",
    "payments.record",
    "refunds.manage",
    "sponsors.manage",
    "vendors.manage",
    "inventory.manage",
    "inventory.operate",
    "exports.reports",
    "analytics.view",
    "privacy.manage",
  ]),
  venue_manager: new Set([
    "venues.manage",
    "calendar.view",
    "events.manage",
    "bookings.manage",
    "bookings.tasks.manage",
    "customers.manage",
    "emails.send",
    "quotes.manage",
    "invoices.issue",
    "vendors.manage",
    "inventory.manage",
    "inventory.operate",
    "exports.reports",
    "analytics.view",
  ]),
  booking_coordinator: new Set([
    "calendar.view",
    "events.manage",
    "bookings.manage",
    "bookings.tasks.manage",
    "customers.manage",
    "emails.send",
    "quotes.manage",
    "invoices.issue",
    "vendors.manage",
    "payments.record",
    "exports.reports",
    "analytics.view",
  ]),
  finance: new Set([
    "calendar.view",
    "quotes.manage",
    "invoices.issue",
    "payments.record",
    "emails.send",
    "refunds.manage",
    "sponsors.manage",
    "exports.reports",
    "analytics.view",
    "privacy.manage",
  ]),
  operations: new Set([
    "calendar.view",
    "bookings.tasks.manage",
    "inventory.operate",
    "exports.reports",
  ]),
  analyst: new Set(["calendar.view", "exports.reports", "analytics.view"]),
};

// Standing in the organisation rather than work inside one half of it. Mirrors
// identity.tenantWidePermissions: who may be employed, what the building is, how the product is
// joined to other systems, the client's own kit, and the records the tenant keeps of itself. They
// are granted when either half of a membership carries them.
const tenantWide = new Set<Permission>([
  "tenant.manage",
  "users.manage",
  "integrations.manage",
  "venues.manage",
  "inventory.manage",
  "privacy.manage",
]);

export function roleAllows(role: WorkspaceRole | undefined, permission: Permission): boolean {
  // A role of none is the absence of a workspace, so it appears in no capability table and is not
  // an error either. Reading it as "nothing allowed" is the whole of what it means.
  const allowed = role && role !== "none" ? permissions[role] : undefined;
  return allowed ? allowed.has(permission) : false;
}

export function workspaceRole(
  membership: Membership | undefined,
  workspace: Workspace,
): WorkspaceRole {
  if (!membership) return "none";
  return workspace === "events" ? membership.eventRole : membership.bookingRole;
}

// Ownership is standing in the organisation rather than a job on one side of it, so it is held when
// either half names it. Mirrors identity.Membership.IsOwner.
export function isOwner(membership: Membership | undefined): boolean {
  return membership?.eventRole === "owner" || membership?.bookingRole === "owner";
}

// What somebody has been given to do, in the words the product uses for the two halves. A side they
// hold no role in is left out rather than described as none, which would read as a mistake to
// somebody who has not seen the product yet. Mirrors tenancy.authorityDescription.
export function authorityLabel(eventRole: WorkspaceRole, bookingRole: WorkspaceRole): string {
  const parts: string[] = [];
  if (eventRole !== "none") parts.push(`${titleCase(eventRole)} for events`);
  if (bookingRole !== "none") parts.push(`${titleCase(bookingRole)} for bookings`);
  return parts.length ? parts.join(" and ") : "Member";
}

// Whether this side of the building is theirs at all, asked before any capability is considered.
// Mirrors identity.Actor.CanEnter.
export function canEnter(membership: Membership | undefined, workspace: Workspace): boolean {
  return workspaceRole(membership, workspace) !== "none";
}

// The coarse gate: may this person do this anywhere in the tenant. Mirrors identity.Actor.Allows,
// and like it this is a union across both halves, so a surface belonging to one side of the
// building must ask membershipAllowsIn rather than this.
export function membershipAllows(
  membership: Membership | undefined,
  permission: Permission,
): boolean {
  return (
    roleAllows(membership?.eventRole, permission) || roleAllows(membership?.bookingRole, permission)
  );
}

// Work inside one workspace, answered by the role held there alone. Standing in the organisation
// is not narrowed this way, which is why the tenant-wide capabilities fall back to the union.
// Mirrors identity.Actor.AllowsIn.
export function membershipAllowsIn(
  membership: Membership | undefined,
  workspace: Workspace,
  permission: Permission,
): boolean {
  if (tenantWide.has(permission)) return membershipAllows(membership, permission);
  return roleAllows(workspaceRole(membership, workspace), permission);
}
